import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Brandmark } from "@/components/Brand";

/**
 * /auth/callback — the single redirect target for every email-based auth
 * flow: OAuth, magic-link, email verification, and password-reset.
 *
 * Three flows land here, in order of precedence:
 *
 *   1. `?error=...` from the provider (OAuth denial, expired link). Surface
 *      it to the user and bounce back to /auth.
 *
 *   2. `?code=...` (PKCE flow) — exchange the authorisation code for a
 *      session via `exchangeCodeForSession`.
 *
 *   3. `#access_token=...` (implicit flow — what Supabase emits when the
 *      project is configured for implicit OAuth). Important nuance: the
 *      Supabase client's `detectSessionInUrl` autoruns at module-load and
 *      parses + strips the hash *before* React mounts. So by the time
 *      this effect fires, the hash is usually gone and the session is
 *      already in localStorage. We detect that by calling `getSession()`
 *      and treating "session present, but no `?code` and no hash" as a
 *      successful auto-parse, not an error.
 *
 * Once a session is confirmed (or we hit an error), we redirect:
 *   recovery → /reset-password
 *   everything else → /  (the protected-route tree handles /onboarding)
 */
export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("Completing sign-in…");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // ---- 1. Provider error ------------------------------------------------
      const errDesc = params.get("error_description") ?? params.get("error");
      if (errDesc) {
        navigate(`/auth?error=${encodeURIComponent(errDesc)}`, { replace: true });
        return;
      }

      const code = params.get("code");
      const type = params.get("type"); // "recovery" | "signup" | "magiclink" | ...
      const hadHash = window.location.hash.includes("access_token");

      try {
        // ---- 2. PKCE: explicit code exchange -------------------------------
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else {
          // ---- 3. Implicit flow OR auto-detected session -------------------
          //
          // `detectSessionInUrl` on the Supabase client autoruns at import
          // time and is async — it parses the hash, stores the session,
          // and strips the URL via history.replaceState. We can't observe
          // the hash directly because by now it's gone, so we wait briefly
          // for the auth state to settle, then read it back.
          //
          // If a session ends up present, we treat it as a success even if
          // we never saw a `?code` or a hash. If after a short wait the
          // session is still null AND there was nothing to exchange in the
          // first place, we bounce back to /auth.
          const { data: { session: existing } } = await supabase.auth.getSession();
          let session = existing;

          if (!session && hadHash) {
            // Hash was present at mount-time but Supabase hasn't finished
            // parsing it yet. Subscribe to the auth event for one tick.
            session = await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                sub.data.subscription.unsubscribe();
                resolve(null);
              }, 3000);
              const sub = supabase.auth.onAuthStateChange((_event, s) => {
                if (s) {
                  clearTimeout(timeout);
                  sub.data.subscription.unsubscribe();
                  resolve(s);
                }
              });
            });
          }

          if (!session) {
            // No code, no hash, no existing session — someone hit the URL
            // directly, or the implicit-flow tokens were malformed.
            navigate("/auth", { replace: true });
            return;
          }
        }

        if (cancelled) return;

        if (type === "recovery") {
          setMessage("Almost there — set a new password.");
          navigate("/reset-password", { replace: true });
          return;
        }

        navigate("/", { replace: true });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Sign-in link is invalid or has expired.";
        navigate(`/auth?error=${encodeURIComponent(msg)}`, { replace: true });
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="flex flex-col items-center gap-4">
        <Brandmark size={36} />
        <div className="text-[13px] text-ink-3">{message}</div>
      </div>
    </div>
  );
}
